/**
 * Typed fetch client for the admin API.
 *
 * Admin sessions travel as `Authorization: Bearer`, never as a cookie. That is
 * the whole reason this file has no CSRF machinery: there is no ambient
 * credential for a third-party page to ride on, and the request is a plain
 * cross-origin fetch that CORS on the API side is free to police.
 *
 * The token is handed to this module by `session.ts` rather than read from
 * storage here, which keeps the dependency pointing one way: session knows about
 * the api client, the api client knows nothing about React or storage.
 */

import { z } from 'zod';
import { describeErrorBody, parseRetryAfter, statusMessage } from './apiErrors.js';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: HttpMethod;
  /** Serialised as JSON. Omit for requests without a body. */
  body?: unknown;
  signal?: AbortSignal;
  /** Send without the Authorization header — the two pre-session auth routes. */
  anonymous?: boolean;
}

/**
 * Anything the API refused, plus the two local failures that are indistinguishable
 * from the caller's point of view: no HTTP answer arrived at all, and an answer
 * arrived in a shape this build does not understand. Both carry status 0.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;
  /** From `Retry-After` on a 429, so a cooldown can honour the server's number. */
  readonly retryAfterSec: number | null;

  constructor(
    status: number,
    message: string,
    code: string | null = null,
    body: unknown = null,
    retryAfterSec: number | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.body = body;
    this.retryAfterSec = retryAfterSec;
  }

  get isTransport(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isRateLimited(): boolean {
    return this.status === 429;
  }
}

export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

/** The message to put in front of a person, whatever went wrong. */
export function errorMessage(value: unknown): string {
  if (isApiError(value)) return value.message;
  if (value instanceof Error && value.message !== '') return value.message;
  return 'Something went wrong. Try again.';
}

// ---------------------------------------------------------------------------
// Ambient state: the bearer token and the "your session is gone" callback.
// ---------------------------------------------------------------------------

let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Called once per 401 on an authenticated route, so the shell can sign out. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export function apiBaseUrl(): string {
  const configured = import.meta.env.VITE_API_URL?.trim() ?? '';
  if (configured === '') {
    throw new ApiError(
      0,
      'VITE_API_URL is not set. Copy .env.example to .env.local and point it at the API.',
      'missing_api_url',
    );
  }
  return configured.replace(/\/+$/, '');
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

function normalisePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204 || response.status === 304) return null;
  const text = await response.text();
  if (text.trim() === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

/** Issues the request and returns the decoded JSON body, or null for an empty one. */
export async function requestJson(path: string, options: RequestOptions = {}): Promise<unknown> {
  const { method = 'GET', body, signal, anonymous = false } = options;

  const headers = new Headers({ Accept: 'application/json' });
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (!anonymous && authToken !== null) headers.set('Authorization', `Bearer ${authToken}`);

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl()}${normalisePath(path)}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    if (cause instanceof ApiError) throw cause;
    // An aborted request is the caller's own doing, not a failure to report.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      'Could not reach the API. Check your connection and try again.',
      'network',
      cause,
    );
  }

  const payload = await readBody(response);

  if (!response.ok) {
    const described = describeErrorBody(payload);
    const error = new ApiError(
      response.status,
      described.message ?? statusMessage(response.status),
      described.code,
      payload,
      parseRetryAfter(response.headers.get('Retry-After'), Date.now()),
    );
    // Only a request that actually carried a token can have had one rejected.
    // Without this guard, signing out cascades: clearing the token lets a
    // still-mounted query refetch, that refetch 401s, and the handler runs
    // again on a session that is already gone.
    if (error.isUnauthorized && !anonymous && authToken !== null) unauthorizedHandler?.();
    throw error;
  }

  return payload;
}

/**
 * Issues the request and validates the answer against a zod schema — the same
 * schemas the API validates its own responses with, so a drift between the two
 * shows up here rather than as an undefined three components deep.
 */
export async function apiRequest<Schema extends z.ZodTypeAny>(
  schema: Schema,
  path: string,
  options: RequestOptions = {},
): Promise<z.output<Schema>> {
  const payload = await requestJson(path, options);
  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    throw new ApiError(
      0,
      'The API returned something this version of the panel does not understand. Reload to pick up the latest build.',
      'response_shape',
      payload,
    );
  }
  return parsed.data;
}
